import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { apiClient } from "@/config/api";
import {
  CreateOrganizationBody,
  CreateSuperAdminParams,
  EditOrganizationBody,
  GetOrganizationByIdParams,
  GetOrganizationBySlugParams,
  GetOrganizationsBody,
  GetOrganizationsResponse,
  GetStreamlineSyncHistoryParams,
  GetStreamlineSyncHistoryResponse,
  GetSuperAdminsParams,
  GetSuperAdminsResponse,
  StreamlineTokensParams,
  TriggerStreamlineSyncParams,
  TriggerStreamlineSyncResponse,
  UploadOrganizationLogoBody,
  GetPropertyCreationSettingsParams,
  GetPropertyCreationSettingsResponse,
  UpdatePropertyCreationSettingsParams,
  CanUpdateSettingsParams,
  CanUpdateSettingsResponse,
} from "@/api/organization/types";
import { HomeownerPropertyCreationSettings } from "@/types/organization";
import { handleApiError } from "@/utils/functions";
import { SyncStartResult, SyncStatus } from "@/types/sync";
import { Organization, OrgTag } from "@/types/organization";

export const getOrganizations = createAsyncThunk(
  "organizations",
  async ({ page }: GetOrganizationsBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/organizations", {
        params: { page },
      });
      return response.data.data as GetOrganizationsResponse;
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Get organizations failed"));
    }
  }
);

export const editOrganization = createAsyncThunk(
  "organizations/:organizationId",
  async (body: EditOrganizationBody, { rejectWithValue }) => {
    try {
      const parsedBody = {
        name: body.name || null,
        description: body.description || null,
        contactPhone: body.contactPhone || null,
        contactEmail: body.contactEmail || null,
      };
      const response = await apiClient.put(
        `/organizations/${body.organizationId}`,
        { ...parsedBody }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Edit organization failed"));
    }
  }
);

export const deactivateOrganization = createAsyncThunk(
  "organizations/:id/deactivate",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/organizations/${id}/deactivate`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Deactivate organization failed")
      );
    }
  }
);

export const reactivateOrganization = createAsyncThunk(
  "organizations/:id/reactivate",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/organizations/${id}/reactivate`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Reactivate organization failed")
      );
    }
  }
);

export const createOrganization = createAsyncThunk(
  "organizations/create",
  async (body: CreateOrganizationBody, { rejectWithValue }) => {
    try {
      const parsedBody = {
        name: body.name || null,
        description: body.description || null,
        slug: body.slug,
        contactPhone: body.contactPhone || null,
        contactEmail: body.contactEmail || null,
      };
      const response = await apiClient.post("/organizations", parsedBody);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Create organization failed")
      );
    }
  }
);

export const uploadOrganizationLogo = createAsyncThunk(
  "organizations/:organizationId/logo",
  async (body: UploadOrganizationLogoBody, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("logo", body.logo);
      const response = await apiClient.put(
        `/organizations/${body.organizationId}/logo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Upload organization logo failed")
      );
    }
  }
);

export const createSuperAdmin = createAsyncThunk(
  "organizations/:organizationId/super-admins",
  async (
    { organizationId, body }: CreateSuperAdminParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post(
        `/organizations/${organizationId}/super-admins`,
        body
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Create super admin failed")
      );
    }
  }
);

export const addStreamlineTokens = createAsyncThunk(
  "organizations/:organizationId/streamline-tokens",
  async (
    { organizationId, body }: StreamlineTokensParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post(
        `/organizations/${organizationId}/streamline-tokens`,
        body
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Add streamline tokens failed")
      );
    }
  }
);

export const getSuperAdmins = createAsyncThunk(
  "organizations/:organizationId/super-admins/get",
  async (
    { organizationId, page = 1 }: GetSuperAdminsParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `/organizations/${organizationId}/super-admins`,
        {
          params: { page },
        }
      );
      return response.data.data as GetSuperAdminsResponse;
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Get super admins failed"));
    }
  }
);

export const getOrganizationById = createAsyncThunk(
  "organizations/:organizationId/get",
  async (
    { organizationId }: GetOrganizationByIdParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(`/organizations/${organizationId}`);
      return response.data.data as Organization;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get organization by ID failed")
      );
    }
  }
);

export const getMyOrganization = createAsyncThunk(
  "organizations/my-organization",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/organizations/my-organization`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Get organization failed"));
    }
  }
);

export const getOrganizationBySlug = createAsyncThunk(
  "organizations/slug/:slug",
  async ({ slug }: GetOrganizationBySlugParams, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/organizations/slug/${slug}`);
      return response.data.data as Organization;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get organization by slug failed")
      );
    }
  }
);

export const triggerStreamlineSync = createAsyncThunk(
  "admin/streamline-sync",
  async (
    { organizationId }: TriggerStreamlineSyncParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post("/admin/streamline-sync", {
        organizationId,
      });
      return response.data.data as TriggerStreamlineSyncResponse;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Trigger streamline sync failed")
      );
    }
  }
);

/**
 * Starts a manual Streamline data sync for the caller's organization (derived
 * from the auth token — no body). 202 -> started; 409 -> a sync is already
 * running for the org (returned as a "conflict" result, not an error, so the
 * button can show the wait message).
 */
export const startStreamlineSync = createAsyncThunk(
  "admin/streamline-sync/start",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<{
        data: { startedAt?: string | null; retryAfterMinutes?: number };
      }>("/admin/streamline-sync");
      const data = response.data.data ?? {};
      const result: SyncStartResult = {
        status: "started",
        startedAt: data.startedAt ?? null,
        retryAfterMinutes: data.retryAfterMinutes,
      };
      return result;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const data = error.response.data?.data ?? {};
        const conflict: SyncStartResult = {
          status: "conflict",
          startedAt: data.startedAt ?? null,
          retryAfterMinutes: data.retryAfterMinutes,
          message:
            data.message ||
            "A Streamline sync is already running for your organization.",
        };
        return conflict;
      }
      return rejectWithValue(
        handleApiError(error, "Failed to start Streamline sync.")
      );
    }
  }
);

/**
 * Reads the org-wide Streamline-sync lock status.
 * GET /admin/streamline-sync/status
 */
export const getStreamlineSyncStatus = createAsyncThunk(
  "admin/streamline-sync/status",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<{ data: SyncStatus }>(
        "/admin/streamline-sync/status"
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Failed to fetch Streamline sync status.")
      );
    }
  }
);

export const getStreamlineSyncHistory = createAsyncThunk(
  "admin/streamline-sync-history",
  async (
    { organizationId, page = 1 }: GetStreamlineSyncHistoryParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get("/admin/streamline-sync-history", {
        params: { organizationId, page },
      });
      return response.data.data as GetStreamlineSyncHistoryResponse;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get streamline sync history failed")
      );
    }
  }
);

// Property Creation Settings
export const getPropertyCreationSettings = createAsyncThunk(
  "organizations/:organizationId/property-creation-settings/get",
  async (
    { organizationId }: GetPropertyCreationSettingsParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `/organizations/${organizationId}/property-creation-settings`
      );
      return response.data.data as GetPropertyCreationSettingsResponse;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get property creation settings failed")
      );
    }
  }
);

export const updatePropertyCreationSettings = createAsyncThunk(
  "organizations/:organizationId/property-creation-settings/update",
  async (
    { organizationId, body }: UpdatePropertyCreationSettingsParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.put(
        `/organizations/${organizationId}/property-creation-settings`,
        body
      );
      return response.data.data as GetPropertyCreationSettingsResponse;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Update property creation settings failed")
      );
    }
  }
);

export const getHomeownerPropertyCreationSettings = createAsyncThunk(
  "organizations/my-organization/property-creation-settings",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/organizations/my-organization/property-creation-settings`
      );
      return response.data.data as HomeownerPropertyCreationSettings;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get property creation settings failed")
      );
    }
  }
);

export const canUpdatePropertyCreationSettings = createAsyncThunk(
  "organizations/:organizationId/property-creation-settings/can-update",
  async (
    { organizationId }: CanUpdateSettingsParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `/organizations/${organizationId}/property-creation-settings/can-update`
      );
      return response.data.data as CanUpdateSettingsResponse;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Check settings update availability failed")
      );
    }
  }
);

// ── Organization property tags ──────────────────────────────────────────────

export const getOrganizationTags = createAsyncThunk(
  "organizations/tags/get",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/organizations/tags");
      return response.data.data.tags as OrgTag[];
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get organization tags failed")
      );
    }
  }
);

export const createOrgTag = createAsyncThunk<
  OrgTag,
  { name: string },
  { rejectValue: { message: string; existingTagId?: string } }
>("organizations/tags/create", async ({ name }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post("/organizations/tags", { name });
    return response.data.data as OrgTag;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // A 409 carries `existingTagId` so the modal can flash the matching row.
      return rejectWithValue({
        message:
          error.response?.data?.error?.message || "Create property tag failed",
        existingTagId: error.response?.data?.error?.details?.existingTagId,
      });
    }
    return rejectWithValue({ message: "Create property tag failed" });
  }
});

export const deleteOrgTags = createAsyncThunk<
  { deletedCount: number; ids: string[] },
  { ids: string[] },
  { rejectValue: string }
>("organizations/tags/delete", async ({ ids }, { rejectWithValue }) => {
  try {
    const response = await apiClient.delete("/organizations/tags", {
      data: { ids },
    });
    // Echo the ids back so the slice can drop them without a refetch.
    return { deletedCount: response.data.data.deletedCount, ids };
  } catch (error) {
    return rejectWithValue(
      handleApiError(error, "Delete property tags failed")
    );
  }
});
